import React from "react";
import { Stack, IconButton, Tooltip, Box, Typography } from "@mui/material";
import type { AssistantMediaPart } from "../../../chat/types";
import { AnimatedCard } from "../../Common/AnimatedCard";

// Inline SVGs so we don't take a hard dep on @mui/icons-material (which is
// not declared in chat-ui/package.json — only @mui/material is).
const DownloadIcon = ({ fontSize }: { fontSize?: "small" }) => (
  <svg viewBox="0 0 24 24" width={fontSize === "small" ? 18 : 24} height={fontSize === "small" ? 18 : 24} fill="currentColor" aria-hidden="true">
    <path d="M5 20h14v-2H5v2zm7-18l-5 5h3v6h4V7h3l-5-5z" />
  </svg>
);

const ContentCopyIcon = ({ fontSize }: { fontSize?: "small" }) => (
  <svg viewBox="0 0 24 24" width={fontSize === "small" ? 18 : 24} height={fontSize === "small" ? 18 : 24} fill="currentColor" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);

interface Props { part: AssistantMediaPart }

export function MediaImageCard({ part }: Props) {
  if (part.status === "expired") {
    return (
      <AnimatedCard variant="inset" sx={{ p: 2, my: 1 }}>
        <Typography variant="body2" color="text.secondary">
          This image is no longer available.
        </Typography>
        {part.prompt && (
          <Tooltip title="Copy prompt">
            <IconButton
              aria-label="Copy prompt"
              size="small"
              onClick={() => navigator.clipboard.writeText(part.prompt!)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </AnimatedCard>
    );
  }

  const aspect = part.width && part.height ? `${part.width} / ${part.height}` : "1 / 1";

  return (
    <AnimatedCard variant="sm" sx={{ p: 1, my: 1, maxWidth: 640 }}>
      <Box sx={{ aspectRatio: aspect, width: "100%", overflow: "hidden", borderRadius: 1.5 }}>
        <img
          src={part.url}
          alt={part.alt}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center" }}>
        <Tooltip title="Download">
          <IconButton
            aria-label="Download"
            component="a"
            href={part.url}
            download
            size="small"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {part.prompt && (
          <Tooltip title="Copy prompt">
            <IconButton
              aria-label="Copy prompt"
              size="small"
              onClick={() => navigator.clipboard.writeText(part.prompt!)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </AnimatedCard>
  );
}
